import React, { useEffect, useState } from "react";
import CustomDropdown from "../../../components/forms/CustomDropdown";
import styles from "./TimesheetsUI.module.css";
import { fmtHours, sumDays } from "./timesheetsConfig";
import { dayName, isWeekend, weekDates } from "../../../utils/weekDates";

function HoursCell({ value, placeholder, disabled, label, onCommit }) {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);

  const commit = () => {
    const hours = text === "" ? 0 : Number(text);
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) return setText(value ?? "");
    if (hours !== (value || 0)) onCommit(hours);
    return undefined;
  };

  return (
    <input
      type="number"
      min="0"
      max="24"
      step="0.25"
      className={styles.hoursInput}
      value={text}
      placeholder={placeholder || ""}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

// The My week grid: one line per project or activity, hours typed straight into the days.
// `scheduled` is { lineKey: { date: hours } }, shown as grey placeholders; `addOptions` is [{ group, value, label }].
export default function WeekGrid({
  weekStart, today, lines, scheduled, leave, dailyCapacity, readOnly,
  addOptions, onCell, onBillable, onRemove, onAdd,
}) {
  const dates = weekDates(weekStart);
  const dueOn = (d) => (isWeekend(d) ? 0 : Math.max(0, dailyCapacity - (leave?.[d] || 0)));
  const dayTotal = (d) => lines.reduce((s, l) => s + (l.days[d] || 0), 0);
  const groups = [...new Set(addOptions.map((o) => o.group))];

  return (
    <div className={styles.gridWrap}>
      <table className={styles.grid}>
        <thead>
          <tr>
            <th className={styles.lineHead}>Project · Activity</th>
            {dates.map((d) => (
              <th key={d} className={`${styles.dayHead} ${d === today ? styles.today : ""} ${isWeekend(d) ? styles.weekend : ""}`}>
                <span>{dayName(d)}</span>
                <span className={styles.dayNum}>{Number(d.slice(8))}</span>
              </th>
            ))}
            <th className={styles.totalHead}>Total</th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.key}>
              <td className={styles.lineTd}>
                <div className={styles.lineCell}>
                <span className={styles.dot} style={{ background: line.color || "#94a3b8" }} />
                <span className={styles.lineText}>
                  <span className={styles.lineName}>{line.name}</span>
                  {line.client_name && <span className={styles.lineSub}>{line.client_name}</span>}
                </span>
                <button
                  type="button"
                  className={`${styles.billable} ${line.is_billable ? "" : styles.nonBillable}`}
                  disabled={readOnly}
                  aria-pressed={line.is_billable}
                  title="Click to change"
                  onClick={() => onBillable(line, !line.is_billable)}
                >
                  {line.is_billable ? "Billable" : "Non-billable"}
                </button>
                </div>
              </td>
              {dates.map((d) => (
                <td
                  key={d}
                  className={`${styles.hoursCell} ${d === today ? styles.today : ""} ${isWeekend(d) ? styles.weekend : ""} ${leave?.[d] ? styles.leave : ""}`}
                >
                  <HoursCell
                    value={line.days[d]}
                    placeholder={scheduled[line.key]?.[d] ? String(scheduled[line.key][d]) : ""}
                    disabled={readOnly}
                    label={`${line.name}, ${dayName(d, "long")} ${Number(d.slice(8))}`}
                    onCommit={(hours) => onCell(line, d, hours)}
                  />
                </td>
              ))}
              <td className={styles.rowTotal}>{sumDays(line.days) ? fmtHours(sumDays(line.days)) : ""}</td>
              <td className={styles.removeCell}>
                {!readOnly && (
                  <button type="button" className={styles.remove} aria-label={`Remove ${line.name}`} onClick={() => onRemove(line)}>×</button>
                )}
              </td>
            </tr>
          ))}
          {!readOnly && (
            <tr>
              <td colSpan={10} className={styles.addRow}>
                {addOptions.length > 0 && (
                  <div className={styles.addLine}>
                    <CustomDropdown
                      value=""
                      placeholder="+ Add a project line"
                      sections={groups.map((g) => ({ label: g, items: addOptions.filter((o) => o.group === g).map((o) => ({ value: o.value, label: o.label })) }))}
                      onChange={(v) => v && onAdd(v)}
                    />
                  </div>
                )}
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td className={styles.loggedLabel}>Logged</td>
            {dates.map((d) => {
              const total = dayTotal(d);
              const due = dueOn(d);
              return (
                <td key={d} className={`${styles.loggedCell} ${d === today ? styles.today : ""} ${total > due && due > 0 ? styles.over : ""} ${total > 0 && due === 0 ? styles.over : ""}`}>
                  <strong>{total ? fmtHours(total) : "—"}</strong>
                  {due > 0 && <span>of {fmtHours(due)}</span>}
                </td>
              );
            })}
            <td className={styles.rowTotal}><strong>{fmtHours(lines.reduce((s, l) => s + sumDays(l.days), 0))}</strong></td>
            <td />
          </tr>
        </tfoot>
      </table>
      <p className={styles.legend}>
        Grey numbers are what Schedule booked; type over them or use Fill from Schedule.
        <span className={styles.legendSwatch}><i className={styles.swatchLeave} /> Leave</span>
        <span className={styles.legendSwatch}><i className={styles.swatchOver} /> Over {fmtHours(dailyCapacity)}</span>
      </p>
    </div>
  );
}
